import {useI18n} from '@/i18n'
import {Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle} from '@/components/ui/sheet'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {formatTimestamp} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {useAuth} from '@/contexts/auth-context'
import type {Span} from '@/types/observability'
import {SpanStatus} from '@/types/observability'
import {ExternalLink} from 'lucide-react'
import {SqlEditor} from '@/components/editors/SqlEditor'

interface SpanDetailSheetProps {
    span: Span | null
    open: boolean
    onOpenChange: (open: boolean) => void
}

function FieldRow({label, value}: {label: string; value: React.ReactNode}) {
    if (value === null || value === undefined || value === '') return null
    return (
        <div className="flex flex-col gap-1 py-2 border-b last:border-b-0">
            <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
            <dd className="text-sm break-all">{value}</dd>
        </div>
    )
}

const TAB_HTTP = 'http'
const TAB_SQL = 'sql'
const TAB_ATTRIBUTES = 'attributes'
const TAB_ERROR = 'error'

export function SpanDetailSheet({span, open, onOpenChange}: SpanDetailSheetProps) {
    const i18n = useI18n()
    const {hasPermission} = useAuth()

    if (!span) return null

    const hasHttp = !!(span.httpMethod || span.httpUrl || span.httpStatusCode != null)
    const hasSql = !!(span.sqlFingerprint || span.sqlStatement)
    const hasAttributes = !!(span.attributes && Object.keys(span.attributes).length > 0)
    const hasError = !!(span.errorCode || span.errorMessage || span.errorLogId)

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-[500px] sm:max-w-[500px] overflow-y-auto" data-testid="span-detail-sheet">
                <SheetHeader>
                    <SheetTitle>{i18n.requestInspector.spanDetail.title}</SheetTitle>
                    <SheetDescription>{span.operationName}</SheetDescription>
                </SheetHeader>

                <Tabs defaultValue="overview" className="mt-4">
                    <TabsList className="flex-wrap h-auto gap-1">
                        <TabsTrigger value="overview" data-testid="tab-overview">
                            {i18n.requestInspector.spanDetail.tabOverview}
                        </TabsTrigger>
                        {hasHttp && (
                            <TabsTrigger value={TAB_HTTP} data-testid="tab-http">
                                {i18n.requestInspector.spanDetail.tabHttp}
                            </TabsTrigger>
                        )}
                        {hasSql && (
                            <TabsTrigger value={TAB_SQL} data-testid="tab-sql">
                                {i18n.requestInspector.spanDetail.tabSql}
                            </TabsTrigger>
                        )}
                        {hasAttributes && (
                            <TabsTrigger value={TAB_ATTRIBUTES} data-testid="tab-attributes">
                                {i18n.requestInspector.spanDetail.tabAttributes}
                            </TabsTrigger>
                        )}
                        {hasError && (
                            <TabsTrigger value={TAB_ERROR} data-testid="tab-error">
                                {i18n.requestInspector.spanDetail.tabError}
                            </TabsTrigger>
                        )}
                    </TabsList>

                    <TabsContent value="overview">
                        <dl>
                            <FieldRow label={i18n.requestInspector.spanDetail.spanId as string} value={
                                <span className="font-mono text-xs">{span.spanId}</span>
                            }/>
                            <FieldRow label={i18n.requestInspector.spanDetail.kind as string} value={
                                <Badge variant="outline">{span.kind}</Badge>
                            }/>
                            <FieldRow label={i18n.requestInspector.spanDetail.operationName as string} value={span.operationName}/>
                            <FieldRow label={i18n.requestInspector.spanDetail.status as string} value={
                                <Badge variant={span.status === SpanStatus.ERROR ? BadgeVariant.DESTRUCTIVE : span.status === SpanStatus.CANCELLED ? BadgeVariant.WARNING : BadgeVariant.SUCCESS}>
                                    {span.status}
                                </Badge>
                            }/>
                            <FieldRow label={i18n.requestInspector.spanDetail.startedAt as string} value={formatTimestamp(span.startedAt)}/>
                            <FieldRow label={i18n.requestInspector.spanDetail.endedAt as string} value={formatTimestamp(span.endedAt)}/>
                            <FieldRow label={i18n.requestInspector.spanDetail.durationMs as string} value={`${span.durationMs}ms`}/>
                            <FieldRow label={i18n.requestInspector.spanDetail.traceId as string} value={
                                <span className="font-mono text-xs">{span.traceId}</span>
                            }/>
                            <FieldRow label={i18n.requestInspector.spanDetail.parentSpanId as string} value={
                                span.parentSpanId ? <span className="font-mono text-xs">{span.parentSpanId}</span> : null
                            }/>
                            <FieldRow label={i18n.requestInspector.spanDetail.sessionId as string} value={span.sessionId}/>
                            <FieldRow label={i18n.requestInspector.spanDetail.actionId as string} value={span.actionId}/>
                            <FieldRow label={i18n.requestInspector.spanDetail.correlationId as string} value={span.correlationId}/>
                            <FieldRow label={i18n.requestInspector.spanDetail.authUserId as string} value={
                                span.authUserId ? <span className="font-mono text-xs">{span.authUserId}</span> : null
                            }/>
                            <FieldRow label={i18n.requestInspector.spanDetail.identityId as string} value={
                                span.identityId ? <span className="font-mono text-xs">{span.identityId}</span> : null
                            }/>
                        </dl>
                    </TabsContent>

                    {hasHttp && (
                        <TabsContent value={TAB_HTTP}>
                            <dl>
                                <FieldRow label={i18n.requestInspector.spanDetail.httpMethod as string} value={
                                    span.httpMethod ? <Badge variant="outline">{span.httpMethod}</Badge> : null
                                }/>
                                <FieldRow label={i18n.requestInspector.spanDetail.httpUrl as string} value={
                                    span.httpUrl ? <span className="font-mono text-xs">{span.httpUrl}</span> : null
                                }/>
                                <FieldRow label={i18n.requestInspector.spanDetail.httpStatusCode as string} value={
                                    span.httpStatusCode != null ? (
                                        <Badge variant={span.httpStatusCode >= 400 ? BadgeVariant.DESTRUCTIVE : span.httpStatusCode >= 300 ? BadgeVariant.WARNING : BadgeVariant.SUCCESS}>
                                            {span.httpStatusCode}
                                        </Badge>
                                    ) : null
                                }/>
                                <FieldRow label={i18n.requestInspector.spanDetail.httpResponseBytes as string} value={
                                    span.httpResponseBytes != null ? `${span.httpResponseBytes} bytes` : null
                                }/>
                            </dl>
                        </TabsContent>
                    )}

                    {hasSql && (
                        <TabsContent value={TAB_SQL}>
                            <dl>
                                <FieldRow label={i18n.requestInspector.spanDetail.sqlFingerprint as string} value={
                                    span.sqlFingerprint ? <span className="font-mono text-xs">{span.sqlFingerprint}</span> : null
                                }/>
                                <FieldRow label={i18n.requestInspector.spanDetail.sqlStatement as string} value={
                                    span.sqlStatement ? (
                                        <SqlEditor sql={span.sqlStatement} onChange={() => {}} readOnly minHeight="40px" maxHeight="300px"/>
                                    ) : null
                                }/>
                                <FieldRow label={i18n.requestInspector.spanDetail.sqlRowsAffected as string} value={
                                    span.sqlRowsAffected != null ? String(span.sqlRowsAffected) : null
                                }/>
                                <FieldRow label={i18n.requestInspector.spanDetail.sqlPoolWaitMs as string} value={
                                    span.sqlPoolWaitMs != null ? `${span.sqlPoolWaitMs}ms` : null
                                }/>
                            </dl>
                        </TabsContent>
                    )}

                    {hasAttributes && (
                        <TabsContent value={TAB_ATTRIBUTES}>
                            <pre className="text-xs font-mono whitespace-pre-wrap bg-muted p-3 rounded mt-2" data-testid="attributes-json">
                                {JSON.stringify(span.attributes, null, 2)}
                            </pre>
                        </TabsContent>
                    )}

                    {hasError && (
                        <TabsContent value={TAB_ERROR}>
                            <dl>
                                <FieldRow label={i18n.requestInspector.spanDetail.errorCode as string} value={span.errorCode}/>
                                <FieldRow label={i18n.requestInspector.spanDetail.errorMessage as string} value={span.errorMessage}/>
                                <FieldRow label={i18n.requestInspector.spanDetail.errorLogId as string} value={
                                    span.errorLogId ? (
                                        hasPermission(permissions.errorLogs.view) ? (
                                            <Button variant="outline" size="sm" asChild data-testid="btn-view-error-log">
                                                <a href={routes.admin.errorLogs.detail(span.errorLogId)} data-testid="link-view-error-log">
                                                    <ExternalLink className="h-3 w-3 mr-1"/>
                                                    {i18n.requestInspector.spanDetail.viewErrorLog}
                                                </a>
                                            </Button>
                                        ) : (
                                            <span className="font-mono text-xs">{span.errorLogId}</span>
                                        )
                                    ) : null
                                }/>
                            </dl>
                        </TabsContent>
                    )}
                </Tabs>
            </SheetContent>
        </Sheet>
    )
}
